import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { api } from '@/lib/api';

export interface ChatMessage {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt?: string;
  metadata?: any;
}

export function useAiChat(loanId?: string) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  const sendMessage = useMutation({
    mutationFn: async (message: string) => {
      // Add user message immediately
      const userMsg: ChatMessage = {
        role: 'user',
        content: message,
        createdAt: new Date().toISOString(),
      };
      setMessages(prev => [...prev, userMsg]);

      const { data } = await api.post(
        '/ai/chat',
        {
          message,
          loanId,
          conversationHistory: messages.slice(-10).map(m => ({ role: m.role, content: m.content })),
        },
        { timeout: 120_000 },
      );
      return data;
    },
    // Never auto-retry a chat send: the global default retries non-4xx errors
    // (e.g. a client timeout) once, which would silently resubmit the message
    // and duplicate both the chat history and the LLM call.
    retry: false,
    onSuccess: data => {
      const assistantMsg: ChatMessage = {
        role: 'assistant',
        content: data.response,
        createdAt: new Date().toISOString(),
        metadata: data.sources,
      };
      setMessages(prev => [...prev, assistantMsg]);
    },
    onError: (error: any) => {
      const errMsg: ChatMessage = {
        role: 'assistant',
        content:
          error?.response?.data?.message || 'Sorry, I encountered an error. Please try again.',
        createdAt: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errMsg]);
    },
  });

  const clearMessages = useCallback(() => setMessages([]), []);

  return {
    messages,
    sendMessage: sendMessage.mutate,
    isLoading: sendMessage.isPending,
    clearMessages,
  };
}

export function useChatHistory(loanId?: string) {
  return useQuery<ChatMessage[]>({
    queryKey: ['chat-history', loanId],
    queryFn: async () => {
      const params = loanId ? `?loanId=${loanId}` : '';
      const { data } = await api.get(`/ai/chat/history${params}`);
      return data;
    },
    enabled: false, // Manual fetch only
  });
}

export function useRiskCommentary(loanId: string) {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/ai/risk-commentary/${loanId}`);
      return data;
    },
  });
}
